import React, { useState, useRef, useCallback } from 'react';
import classNames from 'classnames';
import { Button } from 'components/ui';
import { Text, TEXT_VARIANTS } from 'ui/Typography';
import sentIcon from 'images/sent_swooshs.svg';
import envelopeIcon from 'images/envelope.svg';
import Dismissable from 'components/shared/dismissable';
import useAskTheExpertPosition from './useAskTheExpertPosition';
import useAskTheExpert from './useAskTheExpert';
import AskTheExpertButton from './AskTheExpertButton';
import useMicroFeedback from '../MicroFeedback/useMicroFeedback';
import AskTheExpertMessageInput from './AskTheExpertMessageInput';
import {
  ASK_THE_EXPERT_DESKTOP_MIN_WIDTH,
  ASK_THE_EXPORT_SUCCESS_STATE_DELAY,
} from './constants';

const AskTheExpert = ({
  lesson,
  postUrl,
  sectionId,
  expert,
  expertIconName,
}) => {
  const [open, setOpen] = useState(false);
  const setPosition = useAskTheExpertPosition();

  const includeParams = useRef({
    section_id: sectionId,
  });

  const { setMessage, sending, sent, message, submit } = useAskTheExpert({
    endpoint: postUrl,
    autoResetDelay: ASK_THE_EXPORT_SUCCESS_STATE_DELAY,
    onReset: () => setOpen(false),
    include: includeParams.current,
  });

  useMicroFeedback(
    useCallback(
      selectedText => {
        if (window.innerWidth < ASK_THE_EXPERT_DESKTOP_MIN_WIDTH) return;
        includeParams.current.originally_selected_text = selectedText;
        setMessage(
          t('micro_feedback.feedback_template', { selected: selectedText }),
        );
        setOpen(true);
      },
      [setMessage],
    ),
  );

  return (
    <div ref={setPosition} className="Expert Expert--sticky">
      <div className="ExpertContainer">
        <AskTheExpertButton
          icon={expertIconName}
          disabled={sent}
          onClick={() => setOpen(value => !value)}
        />
        {open && (
          <Dismissable
            dismissCallback={() => !sending && !sent && setOpen(false)}
          >
            <div
              className={classNames(
                'ExpertPopout ExpertPopout--bubble NewDropDown-list Animated Animated--popDown is-open',
                /**
                 * @todo The success container is displayed/hidden using CSS
                 * before is was refactored into React. This behavior remains the
                 * same here because the same markup is being used in the old
                 * Learning Library. Only a handful of customers are still using
                 * it. Once that page is entirely removed, the success container
                 * can be toggled in React using conditional rendering.
                 */
                sent && 'is-sent',
              )}
            >
              <div className="ExpertPopout-formContainer">
                <div className="ExpertPopout-title">
                  <Text variant={TEXT_VARIANTS.BODY}>
                    {t('expert.learner.title')}
                  </Text>
                </div>
                <div className="ExpertForm">
                  <AskTheExpertMessageInput
                    lessonTitle={lesson.title}
                    onChange={setMessage}
                    disabled={sending}
                    value={message}
                  />
                  <div className="p-16">
                    <Button
                      tall
                      customColor
                      fullWidth
                      type="button"
                      onClick={submit}
                      className="ExpertForm-submit"
                      loading={sending}
                      disabled={!message.trim()}
                    >
                      {t('expert.learner.submit', { name: expert.name })}
                    </Button>
                  </div>
                </div>
              </div>
              <div className="ExpertPopout-successContainer">
                <div className="ExpertPopout-image">
                  <img
                    src={envelopeIcon}
                    alt={t('expert.learner.sent_title')}
                  />
                </div>
                <div className="ExpertPopout-waves">
                  <img src={sentIcon} alt="" />
                </div>
                <div className="ExpertPopout-body">
                  <Text component="p" variant={TEXT_VARIANTS.BODY_BOLD}>
                    {t('expert.learner.sent_title')}
                  </Text>
                  <Text component="p" variant={TEXT_VARIANTS.BODY}>
                    {t('expert.learner.sent_body')}
                  </Text>
                </div>
              </div>
            </div>
          </Dismissable>
        )}
      </div>
    </div>
  );
};

// react-on-rails workaround to get hooks to work
export default props => <AskTheExpert {...props} />;
